import { useState, useEffect, useRef } from 'preact/hooks'
import { X, Target, Calendar, Play, CheckCircle, XCircle, Info } from 'lucide-preact'
import { campaignApi } from '../../api/campaign'
import type { CampaignCreateDto, CampaignUpdateDto, CampaignViewDto } from '../../types/campaign'
import { BRAND_CLASSES } from '../../constants/theme'
import { setFormValidationErrors } from '@/utils/errorHandling'
import { useAIForm } from '@/hooks/useAIForm'
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat'
import { TextBoxField, DatePicker, FormActions } from '@/components'

interface CampaignModalProps {
  isOpen: boolean
  onClose: () => void
  selectedWorkspaceId: string
  onCampaignSaved: (campaign: CampaignViewDto) => void
  editCampaign?: CampaignViewDto // If provided, modal is in edit mode
  clientCompanyId: string
}

interface CampaignFormData {
  Name: string
  Description: string
  TargetAudience: string
  StartDate: string
  EndDate: string
  Status: 'Planned' | 'Active' | 'Completed' | 'Cancelled'
}

// Validation constants to match server-side validation
const VALIDATION_CONSTANTS = {
  NAME_MAX_LENGTH: 255, // Typical varchar(255) field
  DESCRIPTION_MAX_LENGTH: 1000, // Reasonable length for description
  TARGET_AUDIENCE_MAX_LENGTH: 500
}

const CAMPAIGN_STATUS_OPTIONS = [
  { value: 'Planned', label: 'Planned', icon: Calendar },
  { value: 'Active', label: 'Active', icon: Play },
  { value: 'Completed', label: 'Completed', icon: CheckCircle },
  { value: 'Cancelled', label: 'Cancelled', icon: XCircle }
] as const

export function CampaignModal({ isOpen, onClose, selectedWorkspaceId, onCampaignSaved, editCampaign, clientCompanyId }: CampaignModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const isEditMode = !!editCampaign
  const modalRef = useRef<HTMLDivElement>(null)

  // AI-integrated form
  const { form, getFormButtonState } = useAIForm<CampaignFormData>({
    mode: 'onChange',
    defaultValues: {
      Name: '',
      Description: '',
      TargetAudience: '',
      StartDate: '',
      EndDate: '',
      Status: 'Planned'
    }
  }, {
    contextSuppressed: !isOpen,
    aiContext: {
      campaignId: editCampaign?.Id,
      location: ChatLocations.CampaignEditor,
      entityId: editCampaign?.Id,
      entityType: ChatEntityTypes.Campaign,
      viewMode: isEditMode ? ChatViewModes.Edit : ChatViewModes.Create,
    },
    fields: [
      {
        aiName: 'campaignName',
        formPath: 'Name',
        displayName: 'Campaign Name',
        fieldType: 'text',
        maxLength: VALIDATION_CONSTANTS.NAME_MAX_LENGTH,
      },
      {
        aiName: 'targetAudience',
        formPath: 'TargetAudience',
        displayName: 'Target Audience',
        fieldType: 'textarea',
        maxLength: VALIDATION_CONSTANTS.TARGET_AUDIENCE_MAX_LENGTH,
      },
      {
        aiName: 'campaignDescription',
        formPath: 'Description',
        displayName: 'Description',
        fieldType: 'textarea',
        maxLength: VALIDATION_CONSTANTS.DESCRIPTION_MAX_LENGTH,
      },
    ],
    isEditMode: isEditMode
  })

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    trigger,
    setError
  } = form

  const currentStatus = watch('Status')
  const currentStartDate = watch('StartDate')

  // Keyboard handler for ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        handleClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, isSubmitting])

  // Trigger validation of EndDate when StartDate changes
  useEffect(() => {
    if (currentStartDate) {
      trigger('EndDate')
    }
  }, [currentStartDate, trigger])

  // Update form data when editCampaign changes
  useEffect(() => {
    if (editCampaign) {
      reset({
        Name: editCampaign.Name || '',
        Description: editCampaign.Description || '',
        TargetAudience: editCampaign.TargetAudience || '',
        StartDate: editCampaign.StartDate ? editCampaign.StartDate.split('T')[0] : '',
        EndDate: editCampaign.EndDate ? editCampaign.EndDate.split('T')[0] : '',
        Status: editCampaign.Status || 'Planned'
      })
    } else {
      // Reset form for create mode
      reset({
        Name: '',
        Description: '',
        TargetAudience: '',
        StartDate: '',
        EndDate: '',
        Status: 'Planned'
      })
    }
  }, [editCampaign, reset])

  const onSubmit = async (data: CampaignFormData) => {
    if (!clientCompanyId) {
      console.error('Missing client company ID')
      return
    }

    setIsSubmitting(true)

    try {
      let response

      if (isEditMode && editCampaign) {
        // Update existing campaign
        const updateData: CampaignUpdateDto = {
          Id: editCampaign.Id,
          ETag: editCampaign.ETag,
          ...data,
          // Convert empty strings to undefined for optional dates
          StartDate: data.StartDate || undefined,
          EndDate: data.EndDate || undefined,
          Description: data.Description || undefined
        }

        response = await campaignApi.updateCampaign(clientCompanyId, selectedWorkspaceId, updateData)
      } else {
        // Create new campaign
        const createData: CampaignCreateDto = {
          ...data,
          // Convert empty strings to undefined for optional dates
          StartDate: data.StartDate || undefined,
          EndDate: data.EndDate || undefined,
          Description: data.Description || undefined
        }

        response = await campaignApi.createCampaign(clientCompanyId, selectedWorkspaceId, createData)
      }

      if (response.error) {
        console.error(`Error ${isEditMode ? 'updating' : 'creating'} campaign:`, response.error)
        // Set form field errors for 422 validation errors
        if (response.status === 422) {
          setFormValidationErrors(response, setError)
        }
        return
      }

      if (response.data) {
        onCampaignSaved(response.data)
        handleClose()
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} campaign:`, error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    reset({
      Name: '',
      Description: '',
      TargetAudience: '',
      StartDate: '',
      EndDate: '',
      Status: 'Planned'
    })
    setIsSubmitting(false)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 flex items-center justify-center p-4">
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="campaign-modal-title"
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${BRAND_CLASSES.BG.PRIMARY_LIGHT}`}>
              <Target className={`w-5 h-5 ${BRAND_CLASSES.TEXT.PRIMARY}`} />
            </div>
            <div>
              <h2 id="campaign-modal-title" className="text-xl font-semibold text-slate-900 dark:text-white">
                {isEditMode ? 'Edit Campaign' : 'New Campaign'}
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                {isEditMode ? 'Update campaign details' : 'Create a new marketing campaign'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close modal"
            className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Campaign Name */}
            <TextBoxField
              control={control}
              name="Name"
              label="Campaign Name"
              required
              showCharCounter
              maxLength={VALIDATION_CONSTANTS.NAME_MAX_LENGTH}
              placeholder="Enter campaign name"
              autoFocus
              rules={{
                required: 'Campaign name is required',
                validate: value => {
                  const trimmed = (value ?? '').trim()
                  if (trimmed.length === 0) return 'Campaign name is required'
                  if (trimmed.length > VALIDATION_CONSTANTS.NAME_MAX_LENGTH) {
                    return `Campaign name must be ${VALIDATION_CONSTANTS.NAME_MAX_LENGTH} characters or less`
                  }
                  return true
                }
              }}
            />

            {/* Target Audience */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Target Audience <span className="text-red-500" aria-hidden="true">*</span>
                </label>
                <div className="group relative">
                  <button
                    type="button"
                    className="flex items-center justify-center p-0 border-0 bg-transparent"
                    aria-label="Show target audience example"
                    onFocus={() => setShowTooltip(true)}
                    onBlur={() => setShowTooltip(false)}
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                  >
                    <Info className="w-4 h-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 cursor-help" aria-hidden="true" />
                  </button>
                  <div
                    role="tooltip"
                    className={`${showTooltip ? 'visible opacity-100' : 'invisible opacity-0'} transition-opacity absolute z-10 left-0 top-6 w-80 p-3 bg-slate-900 dark:bg-slate-700 text-white text-xs rounded-lg shadow-lg`}
                  >
                    <p className="font-medium mb-1">Example:</p>
                    <p className="text-slate-200">"Marketing directors and CMOs at mid-size B2B SaaS companies"</p>
                    <div className="absolute -top-1 left-2 w-2 h-2 bg-slate-900 dark:bg-slate-700 transform rotate-45"></div>
                  </div>
                </div>
              </div>
              <TextBoxField
                control={control}
                name="TargetAudience"
                required
                multiline
                rows={3}
                showCharCounter
                maxLength={VALIDATION_CONSTANTS.TARGET_AUDIENCE_MAX_LENGTH}
                placeholder="Define your target audience: specify key roles, typical company size, and industry type"
                rules={{
                  required: 'Target audience is required',
                  validate: value => {
                    const trimmed = (value ?? '').trim()
                    if (trimmed.length === 0) return 'Target audience is required'
                    if (trimmed.length > VALIDATION_CONSTANTS.TARGET_AUDIENCE_MAX_LENGTH) {
                      return `Target audience must be ${VALIDATION_CONSTANTS.TARGET_AUDIENCE_MAX_LENGTH} characters or less`
                    }
                    return true
                  }
                }}
              />
            </div>

            {/* Campaign Description */}
            <TextBoxField
              control={control}
              name="Description"
              label="Description"
              multiline
              rows={3}
              showCharCounter
              maxLength={VALIDATION_CONSTANTS.DESCRIPTION_MAX_LENGTH}
              placeholder="Describe the campaign objectives and strategy"
              rules={{
                validate: value => {
                  if (value && value.length > VALIDATION_CONSTANTS.DESCRIPTION_MAX_LENGTH) {
                    return `Description must be ${VALIDATION_CONSTANTS.DESCRIPTION_MAX_LENGTH} characters or less`
                  }
                  return true
                }
              }}
            />

            {/* Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DatePicker
                label="Start Date"
                error={errors.StartDate?.message as string}
                {...register('StartDate')}
              />

              <DatePicker
                label="End Date"
                error={errors.EndDate?.message as string}
                {...register('EndDate', {
                  validate: (value) => {
                    if (currentStartDate && value) {
                      const startDate = new Date(currentStartDate)
                      const endDate = new Date(value)

                      if (endDate <= startDate) {
                        return 'End date must be after start date'
                      }
                    }
                    return true
                  }
                })}
              />
            </div>

            {/* Status */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Status <span className="text-red-500" aria-hidden="true">*</span>
              </label>
              <input type="hidden" aria-required="true" {...register('Status', { required: 'Status is required' })} />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {CAMPAIGN_STATUS_OPTIONS.map((option) => {
                  const IconComponent = option.icon
                  const isSelected = currentStatus === option.value

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setValue('Status', option.value, { shouldValidate: true, shouldDirty: true })}
                      className={`p-3 rounded-lg border transition-all ${
                        isSelected
                          ? `${BRAND_CLASSES.BG.PRIMARY} ${BRAND_CLASSES.TEXT.ON_PRIMARY} ${BRAND_CLASSES.BORDER.PRIMARY}`
                          : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <IconComponent className="w-5 h-5" />
                        <span className="text-sm font-medium">{option.label}</span>
                      </div>
                    </button>
                  )
                })}
              </div>
              {errors.Status && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.Status.message}</p>
              )}
            </div>
          </div>

          {/* Footer */}
          <FormActions
            isSubmitting={isSubmitting}
            isSaveDisabled={getFormButtonState(isSubmitting).isSaveDisabled}
            onCancel={handleClose}
            saveText={isEditMode ? (isSubmitting ? 'Updating...' : 'Update Campaign') : (isSubmitting ? 'Creating...' : 'Create Campaign')}
          />
        </form>
      </div>
    </div>
  )
}
